import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { EVENTMANAGEMENTPATH } from '../../../routes/routePaths';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';
import CustomButton from '../../common/Button';
import Step1BasicInfo from './Steps/Step1BasicInfo';
import Step2Sports from './Steps/Step2Sports';
import Step3Configure from './Steps/Step3Configure';
import Step4Review from './Steps/Step4Review';
import {
  EventFormState,
  SportLiteType,
  TournamentSelection,
} from '../../../types/types';
import { EntityType } from '../../../enums/enums';
import { createEvent } from '../../../api/requests/events';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import { getSportsRequest } from '../../../api/requests/sports';
import { UnifiedImage } from '../Facility/FacilityFormModal';
import { bulkUploadFilesRequest } from '../../../api/requests/attachments';
import { getFileOrders } from '../../../pages/Facility';
import {
  mapFormToApiPayload,
  validateBasicInfo,
  validateCategories,
  validateSports,
} from '../../../utils/eventUtils';

const defaultForm: EventFormState = {
  eventName: '',
  description: '',
  eventStartDate: null,
  eventEndDate: null,
  registrationStartDate: null,
  registrationEndDate: null,
  eventAddress: '',
  registrationFee: null,
  selectedSports: [],
  tournaments: {} as TournamentSelection[],
  categoriesBySport: [],
  eventLat: null,
  eventLng: null,
};

const steps = [
  { id: 1, label: 'Basic Info' },
  { id: 2, label: 'Sports' },
  { id: 3, label: 'Configure' },
  { id: 4, label: 'Review' },
];

const EventCreation: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formState, setFormState] = useState<EventFormState>(defaultForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [images, setImages] = useState<UnifiedImage[]>([]);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [sportsData, setSportsData] = useState<SportLiteType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const snackbar = useSnackbar();

  const fetchSports = async () => {
    try {
      setIsLoading(true);
      const response = await getSportsRequest(true);
      if (response?.isSuccess) {
        setSportsData(response?.value || []);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackNavigation = () => {
    navigate(EVENTMANAGEMENTPATH);
  };

  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1: {
        const newErrors = validateBasicInfo(formState);
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
      }
      case 2: {
        const newErrors = validateSports(formState);
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
      }
      case 3: {
        const newErrors = validateCategories(formState);
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
      }
      case 4:
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    setErrors({});

    // Validate current step
    const isValid = validateCurrentStep();

    if (isValid && currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setErrors({});

    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreateEvent = async () => {
    const payload = mapFormToApiPayload(formState);
    try {
      const response = await createEvent(payload);
      if (response && response.isSuccess) {
        if (images && images.length > 0) {
          const uploadResponse = await bulkUploadFilesRequest(
            images.map((img) => img.file!),
            EntityType.Event,
            Number(response?.value),
            'Event Image',
            getFileOrders(images),
          );
          if (uploadResponse) {
            if (
              uploadResponse.value?.failureCount &&
              uploadResponse.value?.failureCount > 0
            ) {
              snackbar.error(
                `${uploadResponse.value.failureCount} image(s) failed to upload.`,
              );
            }
          }
        }

        snackbar.success('Event created successfully');
        navigate(EVENTMANAGEMENTPATH);
      } else {
        console.error('Failed to create event');
        snackbar.error(response?.error || 'Failed to create event');
      }
    } catch (error) {
      console.error('Error creating event:', error);
      snackbar.error('An error occurred while creating the event');
    }
  };

  const removeImage = (index: number) => {
    setFormState((prev) => ({
      ...prev,
      eventImage: null,
    }));
    setTouched((prev) => ({
      ...prev,
      eventImage: false,
    }));
    setImages((prev) => prev.filter((_, idx) => idx !== index));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1BasicInfo
            formState={formState}
            setFormState={setFormState}
            errors={errors}
            images={images}
            setImages={setImages}
            onRemoveImage={removeImage}
            setTouched={setTouched}
          />
        );
      case 2:
        return (
          <Step2Sports
            formState={formState}
            setFormState={setFormState}
            errors={errors}
            sportsData={sportsData}
            isSportsLoading={isLoading}
          />
        );
      case 3:
        return (
          <Step3Configure
            formState={formState}
            setFormState={setFormState}
            errors={errors}
            sportsData={sportsData}
            isSportsLoading={isLoading}
          />
        );
      case 4:
        return (
          <Step4Review
            formState={formState}
            sportsData={sportsData}
            isSportsLoading={isLoading}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    fetchSports();
  }, []);

  return (
    <Container>
      <Header>
        <HeaderContent>
          <BackButton onClick={handleBackNavigation}>
            <ArrowLeftOutlined />
          </BackButton>
          <Title>Create Event</Title>
        </HeaderContent>
      </Header>

      <StepsContainer>
        {steps.map((step, index) => {
          const isActive = currentStep === step.id;
          const isCompleted = currentStep > step.id;

          return (
            <CustomButton
              key={step.id}
              bgColor={
                isCompleted
                  ? COLORS.grey.primary
                  : isActive
                    ? COLORS.grey.medium
                    : COLORS.grey.secondaryLight
              }
              textColor={
                isActive || isCompleted
                  ? COLORS.white.primary
                  : COLORS.grey.dark
              }
              style={{ padding: 24 }}
            >
              {step.label}
            </CustomButton>
          );
        })}
      </StepsContainer>

      <ContentArea>
        {renderStep()}

        {/* Navigation Buttons */}
        <NavigationContainer>
          <LeftButtons>
            {currentStep > 1 && (
              <NavButtons onClick={handlePrevious} variant="outlined">
                Previous
              </NavButtons>
            )}
          </LeftButtons>

          <RightButtons>
            {currentStep < steps.length ? (
              <NavButtons onClick={handleNext} type="primary">
                Next
              </NavButtons>
            ) : (
              <NavButtons onClick={handleCreateEvent} type="primary">
                Create Event
              </NavButtons>
            )}
          </RightButtons>
        </NavigationContainer>
      </ContentArea>
    </Container>
  );
};

const Container = styled.div`
  height: 100%;
`;

const Header = styled.div`
  padding: 20px 24px;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  max-width: 1200px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: ${TEXT_SIZES.XL}px;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const Title = styled.h1`
  //   color: ${COLORS.white.primary};
  font-size: ${TEXT_SIZES.XXL}px;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin: 0;
`;

const StepsContainer = styled.div`
  display: flex;
  gap: 40px;
  background-color: ${COLORS.white.primary};
  border-bottom: 1px solid ${COLORS.grey.light};
  margin: 0 auto;
  padding: 0px 24px;
`;

const ContentArea = styled.div`
  margin: 0 auto;
  padding: 24px;
  background-color: ${COLORS.white.primary};
  min-height: calc(100% - 200px);
  position: relative;
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  margin-top: 32px;
  padding: 16px 0;
  gap: 12px;
  border-top: 1px solid ${COLORS.grey.light};
`;

const LeftButtons = styled.div`
  display: flex;
  gap: 16px;
`;

const RightButtons = styled.div`
  display: flex;
  gap: 16px;
`;

const NavButtons = styled(CustomButton)`
  min-width: 120px;
`;

export default EventCreation;
